import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Phone, MessageCircle, ArrowRight, Sparkles, Instagram, Mail } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" className="relative overflow-hidden pt-28 md:pt-36">
      {/* soft ambient glows */}
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-accent/10 blur-[120px]" />

      <div className="container-x flex flex-col items-center text-center">
        <motion.div initial="hidden" animate="show" variants={stagger(0.14, 0.1)} className="flex w-full flex-col items-center">
          <motion.span variants={fadeUp} className="eyebrow">
            <Sparkles size={13} className="text-accent" /> OMNICHANNEL AUTOMATED INFRASTRUCTURE
          </motion.span>

          {/* MASSIVE product name */}
          <motion.h1
            variants={fadeUp}
            className="mt-7 w-full font-display font-bold leading-[0.86] tracking-[-0.04em] text-ink"
            style={{ fontSize: "clamp(3rem, 13.5vw, 12rem)" }}
          >
            <span className="grad-text">S1EP</span>{" "}
            <span className="text-ink">VELOCITY</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 max-w-2xl text-lg font-medium text-ink-soft md:text-2xl">
            The Sub-60-Second Omnichannel Lead Capture Engine.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-9">
            <motion.button
              data-testid="hero-cta"
              onClick={() => scrollTo("contact")}
              whileHover={{ scale: 1.05, boxShadow: "0 24px 60px -15px rgba(79,70,229,0.55)" }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 350, damping: 16 }}
              className="btn-pill bg-accent px-8 py-4 text-base text-white md:text-lg"
            >
              Send an Enquiry
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Interactive floating phone */}
        <PhoneVisual />
      </div>
    </section>
  );
}

function PhoneVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="group relative mt-16 mb-4 flex justify-center"
    >
      {/* notifications */}
      <motion.div
        initial={{ opacity: 0, x: -40, y: 10 }}
        whileInView={{ opacity: 1, x: 0, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.9, type: "spring", stiffness: 120, damping: 14 }}
        className="absolute -left-4 top-16 z-20 hidden w-60 rounded-2xl border border-slate-100 bg-white/90 p-4 shadow-soft backdrop-blur md:block"
      >
        <ChannelNotification />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 40, y: 10 }}
        whileInView={{ opacity: 1, x: 0, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 1.15, type: "spring", stiffness: 120, damping: 14 }}
        className="absolute -right-4 top-44 z-20 hidden w-60 rounded-2xl border border-slate-100 bg-white/90 p-4 shadow-soft backdrop-blur md:block"
      >
        <div className="flex items-center gap-3">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-accent text-white">
            <Phone size={17} />
            <span className="absolute inset-0 rounded-full bg-accent animate-pulseRing" />
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-ink">AI Voice Call</p>
            <p className="text-xs text-ink-muted">Connecting lead… 0:52</p>
          </div>
        </div>
      </motion.div>

      {/* the phone */}
      <motion.div
        whileHover={{ y: -18 }}
        animate={{ y: [0, -10, 0] }}
        transition={{ y: { duration: 5, repeat: Infinity, ease: "easeInOut" } }}
        className="relative z-10"
      >
        <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-accent/20 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
        <div className="relative h-[430px] w-[220px] rounded-[2.6rem] border-[6px] border-ink bg-ink p-2 shadow-[0_40px_90px_-30px_rgba(15,23,42,0.5)] transition-shadow duration-500 group-hover:shadow-glow md:h-[480px] md:w-[248px]">
          <div className="flex h-full w-full flex-col overflow-hidden rounded-[2rem] bg-gradient-to-b from-white to-pearl">
            <div className="mx-auto mt-2 h-1.5 w-16 rounded-full bg-slate-200" />
            <div className="flex-1 space-y-3 p-4">
              <div className="rounded-2xl bg-accent p-4 text-left text-white shadow-glow">
                <p className="text-[10px] font-semibold uppercase tracking-widest opacity-80">Incoming Lead</p>
                <p className="mt-1 text-sm font-bold">Web form → Engaged in 52s</p>
              </div>
              <div className="rounded-2xl bg-white p-3 text-left shadow-sm">
                <p className="text-xs font-semibold text-ink">AI Agent</p>
                <p className="text-[11px] text-ink-muted">“Hi Alex, thanks for reaching out! Are you free for a quick call?”</p>
              </div>
              <div className="ml-auto w-4/5 rounded-2xl bg-emerald-500 p-3 text-left text-white">
                <p className="text-[11px]">“Yes — call me now.”</p>
              </div>
              <div className="rounded-2xl bg-white p-3 text-left shadow-sm">
                <p className="text-[11px] text-ink-muted">✅ Calendar slot locked · 2:30 PM</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}


// Cycles through omnichannel auto-replies (WhatsApp, Instagram, Mail) with the same message.
const CHANNELS = [
  { name: "WhatsApp", Icon: MessageCircle, bg: "bg-emerald-500" },
  { name: "Instagram", Icon: Instagram, bg: "bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400" },
  { name: "Mail", Icon: Mail, bg: "bg-blue-500" },
];

function ChannelNotification() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % CHANNELS.length), 2200);
    return () => clearInterval(id);
  }, []);

  const { name, Icon, bg } = CHANNELS[index];

  return (
    <div className="relative h-[44px]">
      <AnimatePresence mode="wait">
        <motion.div
          key={name}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3"
        >
          <span className={`flex h-9 w-9 items-center justify-center rounded-full text-white ${bg}`}>
            <Icon size={18} />
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-ink">{name}</p>
            <p className="text-xs text-ink-muted">Auto-reply sent in 4s</p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
